import { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight, ChevronUp, ShieldCheck, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import type { TemplateSection, TemplateQuestion } from '@/api/hooks/useAttestations';
import type { QuestionUpdatePatch } from '@/api/hooks/useTemplateEdit';
import { CERT_TYPES, type CertType } from '@grc/shared';

const RESPONSE_TYPES = ['yes_no', 'multiple_choice', 'free_text', 'scale_1_5', 'file_upload', 'date', 'number'];

interface QuestionHandlers {
  onDeleteQuestion: (questionId: string) => void;
  onUpdateQuestion: (questionId: string, patch: QuestionUpdatePatch) => void;
  onUpdateBypassEligibility?: (questionId: string, certTypes: string[]) => void;
  allowBypass?: boolean;
}

interface Props extends QuestionHandlers {
  section: TemplateSection;
  onAddQuestion: (sectionId: string, text: string, responseType: string, maxScore: number, isRequired: boolean) => void;
  onReorderQuestions: (sectionId: string, orderedIds: string[]) => void;
  noSectionHeader?: boolean;
}

// One sortable question row. Drag handle → reorder (mouse + keyboard); move-up/down buttons as the
// a11y fallback. All S2 inline controls preserved: Required pill, inline Score, inline answer-type,
// click-text edit form, bypass-eligibility editor.
function SortableQuestion({
  q, isFirst, isLast, onMoveQuestion, h,
}: {
  q: TemplateQuestion;
  isFirst: boolean;
  isLast: boolean;
  onMoveQuestion: (questionId: string, dir: -1 | 1) => void;
  h: QuestionHandlers;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(q.question_text);
  const [editGuidance, setEditGuidance] = useState(q.guidance_text ?? '');
  const [editingBypass, setEditingBypass] = useState(false);
  const [editBypassCerts, setEditBypassCerts] = useState<Set<CertType>>(new Set());

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  function startEdit() { setEditText(q.question_text); setEditGuidance(q.guidance_text ?? ''); setEditing(true); }
  function save() {
    if (!editText.trim()) return;
    h.onUpdateQuestion(q.id, { question_text: editText.trim(), guidance_text: editGuidance.trim() || null });
    setEditing(false);
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2 bg-bg-canvas px-3 py-3">
      {/* Drag handle — focusable NON-button (attributes supply role/tabIndex) so keyboard drag works. */}
      <span
        ref={setActivatorNodeRef}
        className="mt-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-text-muted hover:text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan active:cursor-grabbing"
        aria-label="Drag to reorder question (or focus and press Space, then arrow keys)"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      {/* Move up/down — robust keyboard/a11y fallback. */}
      <div className="mt-0.5 flex shrink-0 flex-col">
        <button type="button" onClick={() => onMoveQuestion(q.id, -1)} disabled={isFirst} aria-label="Move question up" className="rounded p-0.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
        <button type="button" onClick={() => onMoveQuestion(q.id, 1)} disabled={isLast} aria-label="Move question down" className="rounded p-0.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
      </div>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="space-y-2">
            <Input value={editText} onChange={(e) => setEditText(e.target.value)} className="text-sm" />
            <Input value={editGuidance} onChange={(e) => setEditGuidance(e.target.value)} placeholder="Guidance text (optional)" className="text-sm" />
            <div className="flex gap-2">
              <Button size="sm" variant="default" onClick={save}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-sm text-text-primary cursor-pointer hover:underline" onClick={startEdit}>{q.question_text}</p>
            {q.guidance_text && <p className="text-xs text-text-secondary">{q.guidance_text}</p>}
            {h.allowBypass && h.onUpdateBypassEligibility && (
              editingBypass ? (
                <div className="mt-2 space-y-1.5">
                  <p className="text-[10px] font-medium text-text-secondary uppercase tracking-wide">Cert types that cover this question</p>
                  <div className="flex flex-wrap gap-1">
                    {CERT_TYPES.map((ct) => {
                      const active = editBypassCerts.has(ct);
                      return (
                        <button
                          key={ct}
                          type="button"
                          onClick={() => setEditBypassCerts((prev) => { const next = new Set(prev); if (next.has(ct)) next.delete(ct); else next.add(ct); return next; })}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-medium border transition-colors ${active ? 'border-accent-cyan bg-accent-cyan/15 text-accent-cyan' : 'border-border-subtle bg-bg-surface text-text-muted hover:border-accent-cyan/50'}`}
                        >
                          {ct.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-6 text-xs px-2" onClick={() => { h.onUpdateBypassEligibility!(q.id, [...editBypassCerts]); setEditingBypass(false); }}>Save</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingBypass(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-1">
                  <ShieldCheck className="h-3 w-3 text-text-muted" />
                  {q.bypass_eligibility?.cert_types?.length ? (
                    <>
                      {q.bypass_eligibility.cert_types.slice(0, 3).map((ct) => (
                        <Badge key={ct} variant="muted" className="text-[9px] py-0 px-1">{ct}</Badge>
                      ))}
                      {q.bypass_eligibility.cert_types.length > 3 && (
                        <span className="text-[10px] text-text-muted">+{q.bypass_eligibility.cert_types.length - 3} more</span>
                      )}
                    </>
                  ) : (
                    <span className="text-[10px] text-text-muted">No bypass cert types</span>
                  )}
                  <Button variant="ghost" size="icon" className="h-5 w-5 ml-0.5 text-text-muted" onClick={() => { setEditingBypass(true); setEditBypassCerts(new Set((q.bypass_eligibility?.cert_types ?? []) as CertType[])); }}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={() => h.onUpdateQuestion(q.id, { is_required: !q.is_required })}
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium border transition-colors ${q.is_required ? 'border-accent-amber/50 bg-accent-amber/10 text-accent-amber' : 'border-border-subtle bg-bg-surface text-text-muted hover:border-accent-amber/40'}`}
          aria-pressed={q.is_required}
          title={q.is_required ? 'Required — click to make optional' : 'Optional — click to make required'}
        >
          {q.is_required ? 'Required' : 'Optional'}
        </button>
        <span className="flex items-center gap-1 rounded border border-border-subtle bg-bg-surface px-1.5 py-0.5">
          <input
            type="number" min={0} max={10} step={1}
            defaultValue={q.max_score}
            key={`score-${q.id}-${q.max_score}`}
            aria-label="Question score"
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n) && n >= 0 && n <= 10) {
                if (n !== q.max_score) h.onUpdateQuestion(q.id, { max_score: n });
              } else {
                e.target.value = String(q.max_score);
                toast.error('Score must be a whole number between 0 and 10.');
              }
            }}
            className="w-8 bg-transparent text-[11px] text-text-primary focus:outline-none"
          />
          <span className="text-[10px] text-text-muted">pts</span>
        </span>
        <select
          value={q.response_type}
          onChange={(e) => h.onUpdateQuestion(q.id, { response_type: e.target.value })}
          aria-label="Answer type"
          className="rounded border border-border-subtle bg-bg-surface-2 px-1.5 py-0.5 text-[11px] text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-sky/50 capitalize"
        >
          {RESPONSE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-text-muted hover:text-destructive" onClick={() => h.onDeleteQuestion(q.id)} aria-label="Delete question">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function QuestionEditor({ section, onAddQuestion, onReorderQuestions, noSectionHeader = false, ...h }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newText, setNewText] = useState('');
  const [newType, setNewType] = useState('yes_no');
  const [newScore, setNewScore] = useState(10);
  const [newRequired, setNewRequired] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleAdd() {
    if (!newText.trim()) return;
    onAddQuestion(section.id, newText.trim(), newType, newScore, newRequired);
    setNewText(''); setNewType('yes_no'); setNewScore(10); setNewRequired(true); setAddingQuestion(false);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldI = section.questions.findIndex((q) => q.id === active.id);
    const newI = section.questions.findIndex((q) => q.id === over.id);
    if (oldI < 0 || newI < 0) return;
    onReorderQuestions(section.id, arrayMove(section.questions, oldI, newI).map((q) => q.id));
  }
  function moveQuestion(id: string, dir: -1 | 1) {
    const i = section.questions.findIndex((q) => q.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= section.questions.length) return;
    onReorderQuestions(section.id, arrayMove(section.questions, i, j).map((q) => q.id));
  }

  const questionsList = (
    <div className="bg-bg-canvas divide-y divide-border-subtle">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={section.questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
          {section.questions.map((q, i) => (
            <SortableQuestion
              key={q.id}
              q={q}
              isFirst={i === 0}
              isLast={i === section.questions.length - 1}
              onMoveQuestion={moveQuestion}
              h={h}
            />
          ))}
        </SortableContext>
      </DndContext>

      {addingQuestion ? (
        <div className="px-4 py-3 space-y-2">
          <Textarea value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="Question text..." className="text-sm min-h-[60px]" />
          <div className="flex gap-2 items-center">
            <select value={newType} onChange={(e) => setNewType(e.target.value)} className="rounded-md border border-border-subtle bg-bg-surface-2 px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-sky/50 capitalize">
              {RESPONSE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <Label className="text-xs text-text-secondary">Score:</Label>
            <Input type="number" value={newScore} onChange={(e) => setNewScore(Number(e.target.value))} className="w-16 text-sm h-8" min={0} max={1000} />
            <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer select-none ml-1">
              <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} className="accent-accent-cyan" />
              Required
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="default" onClick={handleAdd} disabled={!newText.trim()}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAddingQuestion(false); setNewText(''); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2">
          <Button variant="ghost" size="sm" className="text-xs text-text-secondary h-7" onClick={() => setAddingQuestion(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add question
          </Button>
        </div>
      )}
    </div>
  );

  if (noSectionHeader) return questionsList;

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <button type="button" className="w-full flex items-center justify-between px-4 py-3 bg-bg-surface hover:bg-bg-hover text-left" onClick={() => setExpanded((e) => !e)}>
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-text-muted" />
          {expanded ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
          <span className="text-sm font-medium text-text-primary">{section.name}</span>
        </div>
        <Badge variant="muted" className="text-[10px]">{section.questions.length} questions</Badge>
      </button>
      {expanded && questionsList}
    </div>
  );
}
